import React, { useState, useEffect } from 'react';
import env from '../config/env';

interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: string | null;
  alt?: string;
  className?: string;
  fallbackSrc?: string;
  type?: 'avatar' | 'default';
}

const Image: React.FC<ImageProps> = ({ 
  src, 
  alt = 'Image', 
  className = '', 
  fallbackSrc, 
  type = 'default',
  ...props 
}) => {
  const [imgSrc, setImgSrc] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);

  // Default fallback images based on type
  const defaultFallback = type === 'avatar' 
    ? '/assets/images/avatars/blank.png' 
    : '/assets/images/blank.png';

  const finalFallback = fallbackSrc || defaultFallback;

  useEffect(() => {
    // If no source provided, use fallback immediately
    if (!src) {
      setImgSrc(finalFallback);
      setHasError(true);
      return;
    }

    // Reset error state when src changes
    setHasError(false);

    // If it's already a full URL or data URI, use as is
    if (src.startsWith('http') || src.startsWith('data:')) {
      setImgSrc(src);
      return;
    }

    // Construct full URL for relative paths
    // Remove /api suffix from base URL to get the root URL (e.g. https://domain.com/backend)
    const baseUrl = env.apiUrl.replace(/\/api\/?$/, '');
    
    // Ensure src doesn't start with / to avoid double slashes when joining
    const cleanSrc = src.startsWith('/') ? src.substring(1) : src;
    
    // If the path already includes 'uploads/', just append to base
    // Otherwise assume it needs to be in 'uploads/' folder
    if (cleanSrc.startsWith('uploads/') || cleanSrc.startsWith('assets/')) {
      setImgSrc(`${baseUrl}/${cleanSrc}`);
    } else {
      setImgSrc(`${baseUrl}/uploads/${cleanSrc}`);
    }
  }, [src, finalFallback]);

  const handleError = () => {
    if (!hasError) {
      setImgSrc(finalFallback);
      setHasError(true);
    }
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={handleError}
      {...props}
    />
  );
};

export default Image;
